import * as yup from "yup";
import {
  validateDate,
  validateTime,
} from "@common/components/helpers/datetime";
import { validateTimeNotInFuture } from "../components/helpers/datetime";

export const VALID_POLICE_INVOLVED = yup
  .boolean()
  .required("Please select whether police is involved");

export const VALID_DATE = yup
  .string()
  .required("Date is required")
  .test("is-valid-date", "Date is not valid", validateDate);

export const VALID_TIME = yup
  .string()
  .required("Time is required")
  .test("is-valid-time", "Time is not valid", validateTime);

export const TIME_NOT_IN_FUTURE = yup
  .string()
  .required("Time is required")
  .test("not-in-future", "Time cannot be in the future", function (time) {
    return validateTimeNotInFuture(time, this.parent.date);
  });

export const VALID_OUTCOME = yup.string().required("Outcome is required");

export const VALID_ACTIVITY_TYPE = yup
  .string()
  .required("Activity type is required");

export const VALID_CUSTOMERS_ARRAY = yup
  .array()
  .of(yup.object())
  .required("Persons involved are required.")
  .min(1, "Persons involved are required.");

export const VALID_DESCRIPTION = yup
  .string()
  .required("Description is required.");

export const VALID_CCTV = yup
  .boolean()
  .required("You must say if the incident was captured by ccTV.");

export const VALID_INCIDENT_NUMBER = yup
  .string()
  .when("policeInvolved", {
    is: true,
    then: (schema) =>
      schema.when("servicesDidNotAttend", {
        is: false,
        then: (schema) => schema.required("Incident number is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    otherwise: (schema) => schema.notRequired(),
  })
  .when(["esCalled", "esTypes", "servicesDidNotAttend"], {
    is: (esCalled, esTypes, servicesDidNotAttend) =>
      esCalled === true && esTypes.includes("police") && !servicesDidNotAttend,
    then: (schema) => schema.required("Incident number is required."),
  });

export const VALID_COMMENT = yup.string();

export const VALID_INCIDENT_TYPE = yup.object().shape({
  id: yup.number().positive().min(1, "Incident type is required."),
  name: yup.string(),
  detailsRequired: yup.boolean(),
  policeIncident: yup.boolean(),
});

export const VALID_OFFICER = yup
  .mixed()
  .nullable()
  .when(["esCalled", "esTypes", "servicesDidNotAttend"], {
    is: (esCalled, esTypes, servicesDidNotAttend) =>
      esCalled === true && esTypes.includes("police") && !servicesDidNotAttend,
    then: (schema) => schema.required("Officer is required."),
  })
  .when("policeInvolved", {
    is: true,
    then: (schema) =>
      schema.when("servicesDidNotAttend", {
        is: false,
        then: (schema) => schema.notRequired("Officer is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    otherwise: (schema) => schema.notRequired(),
  });

export const VALID_SERVICES_ARRIVED_TIME = yup
  .string()
  .when("esCalled", {
    is: true,
    then: (schema) =>
      schema.when("servicesDidNotAttend", {
        is: (servicesDidNotAttend) => servicesDidNotAttend === false,
        then: (schema) =>
          schema
            .test("validDate", "Time is not valid.", validateTime)
            .required(
              "Arrival time is required, unles the emergency services did not attend.",
            ),
      }),
  })
  .when(["allowFuture", "policeInvolved"], {
    is: (allowFuture, policeInvolved) => !allowFuture && policeInvolved,
    then: (schema) =>
      schema.when("servicesDidNotAttend", {
        is: (servicesDidNotAttend) => servicesDidNotAttend === false,
        then: (schema) =>
          schema
            .required(
              "Arrival time is required, unles the emergency services did not attend",
            )
            .test("validDate", "Time is not valid.", validateTime),
      }),
  });

export const VALID_IS_INJURED = yup
  .boolean()
  .required("You must say if anyone was injured.");

export const VALID_INJURY_DETAILS = yup.string().when("injuries", {
  is: true,
  then: (schema) => schema.required("You must enter details of the injuries."),
});

export const VALID_ES_CALLED = yup
  .boolean()
  .required("You must say if emergency services were called.");

export const VALID_ES_TYPES = yup
  .array()
  .of(
    yup
      .string()
      .oneOf(
        ["police", "ambulance", "fire"],
        "At least one emergency service is required.",
      ),
  )
  .when("esCalled", {
    is: true,
    then: (schema) =>
      schema
        .required("Emergency services types are required.")
        .min(1, "At least one emergency service is required."),
  });

export const VALID_AMBULANCE_DETAILS = yup
  .string()
  .when(["esCalled", "esTypes", "servicesDidNotAttend"], {
    is: (esCalled, esTypes, servicesDidNotAttend) =>
      esCalled === true &&
      esTypes.includes("ambulance") &&
      !servicesDidNotAttend,
    then: (schema) => schema.required("Ambulance details are required."),
  });

export const VALID_TIME_SERVICES_CONTACTED = yup
  .string()
  .when("esCalled", {
    is: true,
    then: (schema) =>
      schema
        .test("validTime", "Time is not valid.", validateTime)
        .required("Emergency services called time is required."),
  })
  .when(["allowFuture", "policeInvolved"], {
    is: (allowFuture, policeInvolved) => !allowFuture && policeInvolved,
    then: (schema) =>
      schema
        .required("Time police contacted is required")
        .test("is-valid-time", "Time is not valid", validateTime),
    otherwise: (schema) => schema.notRequired(),
  });

export const VALID_SERVICES_DID_NOT_ATTEND = yup
  .boolean()
  .when("esCalled", {
    is: true,
    then: (schema) =>
      schema.when("timeServicesArrived", {
        is: (timeServicesArrived) => timeServicesArrived === "",
        then: (schema) =>
          schema.required(
            "Did not attend is required when there is no arrival time.",
          ),
      }),
  })
  .when("policeInvolved", {
    is: true,
    then: (schema) =>
      schema.when("timeServicesArrived", {
        is: (timeServicesArrived) => timeServicesArrived === "",
        then: (schema) =>
          schema.required(
            "Did not attend is required when there is no arrival time",
          ),
      }),
  });

export const VALID_INSTALLATION_ID = yup
  .object()
  .shape({
    installationId: yup.string(),
    installationGuid: yup.string(),
  })
  .test(
    "at-least-one-required",
    "Either installationId or installationGuid is required",
    (value) => !!(value.installationId || value.installationGuid),
  );
